// What a session needs before its first scene can draw, fetched while the sign-in screen waits: the model manifest,
// the model files of the scene the page opens on (the scene this browser was last on, as the console reopens it, else
// the home site's in firstFiles.json: the corpus leader's home as the router records it) and the content bundle the
// server runs (kept in IndexedDB, so IDENTIFY names it and READY carries none). Everything is asked at low priority into
// the HTTP cache (the content into the store's own cache), so the boot after a sign-in finds it there. Imported by the
// sign-in entry: no engine code.

import { keepBundle, keptHash } from './adapter/contentCache';
import defaults from './firstFiles.json';

const KEY = 'gc.beta.firstScene';
/** the files fetched at once */
const PARALLEL = 4;

/** The models a scene loads: their ids, the variants asked for, the files (paths from the manifest). */
export interface ScenePlan {
  models: string[];
  variants?: Record<string, readonly string[]>;
  files: string[];
}

/** Keep what the scene open now loads: the next page opens on it (the console's last view), so its next boot loads
 *  these models while it waits for the session and its sign-in screen fetches the files. */
export function rememberScene(plan: ScenePlan): void {
  try {
    if (plan.files.length) localStorage.setItem(KEY, JSON.stringify(plan));
  } catch {
    // private mode or full storage: the next sign-in fetches the home site's
  }
}

/** What the scene the page opens on is likely to load: this browser's last scene, else the home site's. */
export function firstScenePlan(): ScenePlan {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Partial<ScenePlan> | null;
    if (v && Array.isArray(v.models) && Array.isArray(v.files)) return { models: v.models, variants: v.variants, files: v.files };
  } catch {
    // unreadable: the home site's
  }
  return defaults as ScenePlan;
}

const low = { priority: 'low', credentials: 'same-origin' } as RequestInit;

/** The content bundle the server runs, into the store's cache unless it is the one kept. */
async function prefetchContent(): Promise<void> {
  const cfg = (await fetch('/api/controller/config', low)
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null)) as { contentHash?: unknown } | null;
  const hash = cfg?.contentHash;
  if (typeof hash !== 'string' || !hash || (await keptHash()) === hash) return;
  const text = await fetch(`/api/content/${hash}`, low)
    .then((r) => (r.ok ? r.text() : null))
    .catch(() => null);
  if (text) await keepBundle(hash, text);
}

/** The manifest, then the first scene's model files by their hashed addresses, into the HTTP cache. */
async function prefetchModels(): Promise<void> {
  const base = `${import.meta.env.BASE_URL}models/`;
  const m = (await fetch(`${base}manifest.json`, low)
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null)) as { hashes?: Record<string, string> } | null;
  if (!m) return;
  const queue = firstScenePlan().files.filter((f) => !m.hashes || m.hashes[f]);
  const next = async (): Promise<void> => {
    for (let f = queue.shift(); f !== undefined; f = queue.shift()) {
      const h = m.hashes?.[f];
      await fetch(h ? `${base}${f}?v=${h}` : base + f, low)
        .then((r) => r.arrayBuffer())
        .catch(() => null);
    }
  };
  await Promise.all(Array.from({ length: PARALLEL }, next));
}

/** Fetch what the first scene needs while the sign-in screen shows. */
export function prefetchWorld(): void {
  void prefetchContent();
  void prefetchModels();
}
